
import React from 'react';
import { Heart, Trash2 } from 'lucide-react';
import { Memory } from '@/types/memory';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/auth';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

interface MemoryCardProps {
  memory: Memory;
  userCanLike: boolean;
  onLikeToggle: () => void;
  onDelete?: () => void;
}

const MemoryCard: React.FC<MemoryCardProps> = ({ memory, userCanLike, onLikeToggle, onDelete }) => {
  const { user } = useAuth();
  const memoryUrl = supabase.storage.from('memories').getPublicUrl(memory.file_path).data.publicUrl;
  const isOwner = user && memory.user_id === user.id;
  
  const handleLikeToggle = async () => {
    if (!user || !userCanLike) return;
    
    try {
      if (memory.user_has_liked) {
        // Unlike
        await supabase
          .from('memory_likes')
          .delete()
          .match({ 
            memory_id: memory.id,
            user_id: user.id 
          });
      } else {
        // Like
        await supabase
          .from('memory_likes')
          .insert({
            memory_id: memory.id,
            user_id: user.id
          });
      }
      
      onLikeToggle();
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleDelete = async () => {
    if (!isOwner) return;

    if (confirm('Are you sure you want to delete this memory? This action cannot be undone.')) {
      try {
        // First delete all likes for this memory
        const { error: likesError } = await supabase
          .from('memory_likes')
          .delete()
          .eq('memory_id', memory.id);
          
        if (likesError) throw likesError;
        
        // Then delete the memory record itself
        const { error: memoryError } = await supabase
          .from('memories')
          .delete()
          .eq('id', memory.id);
          
        if (memoryError) throw memoryError;
        
        // Finally delete the file from storage
        const { error: storageError } = await supabase
          .storage
          .from('memories')
          .remove([memory.file_path]);
          
        if (storageError) throw storageError;
        
        toast.success('Memory deleted successfully');
        
        // Call the onDelete callback to refresh the list
        if (onDelete) onDelete();
      } catch (error) {
        console.error('Error deleting memory:', error);
        toast.error('Failed to delete memory');
      }
    }
  };

  const timeAgo = formatDistanceToNow(new Date(memory.created_at), { addSuffix: true });

  return (
    <Card className="overflow-hidden h-full flex flex-col">
      <div className="relative pt-[56.25%] overflow-hidden">
        {memory.file_type === 'video' ? (
          <video 
            src={memoryUrl} 
            className="absolute top-0 left-0 w-full h-full object-cover"
            controls
          />
        ) : (
          <img 
            src={memoryUrl} 
            alt={memory.caption || 'Trip memory'} 
            className="absolute top-0 left-0 w-full h-full object-cover"
          />
        )}
      </div>
      
      <CardContent className="flex-grow p-4">
        {memory.caption && (
          <p className="text-sm">{memory.caption}</p>
        )}
        <p className="text-xs text-gray-500 mt-2">{timeAgo}</p>
      </CardContent>
      
      <CardFooter className="p-4 pt-0 flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "flex items-center gap-1 px-2",
            memory.user_has_liked && "text-red-500"
          )}
          onClick={handleLikeToggle}
          disabled={!userCanLike}
        >
          <Heart 
            className={cn(
              "h-4 w-4", 
              memory.user_has_liked && "fill-current"
            )} 
          />
          <span>{memory.like_count || 0}</span>
        </Button>
        
        {isOwner && (
          <Button
            variant="ghost" 
            size="sm"
            className="text-red-500 flex items-center gap-1"
            onClick={handleDelete}
          >
            <Trash2 className="h-4 w-4" />
            <span>Delete</span>
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default MemoryCard;
